import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import styled from 'styled-components';
import { CopyAssistDropdown } from 'modules/ai/copyAssist/components/dropdown/CopyAssistDropdown';
import { CopyAssistDropdownBodyContent } from 'modules/ai/copyAssist/components/dropdown/CopyAssistDropdownBodyContent';
import { CopyAssistDropdownHeader } from 'modules/ai/copyAssist/components/dropdown/CopyAssistDropdownHeader';
import { Suggestion } from 'modules/ai/nba/types/nbaQuery';
import { getMessageHeaderByType } from 'modules/ai/nba/utils';
import { getSelectedSuggestion } from 'modules/ai/nba/utils/recommendedContents';
import { MessageMedium } from 'modules/domains/campaign';
import { ChevronDown16 } from 'ui/icons/aurora/16px/ChevronDown16';
import { ChevronUp16 } from 'ui/icons/aurora/16px/ChevronUp16';
import { TriggerButton } from 'ui/menu/Styled';
import { body2, smallShadow, spacing } from 'ui/theme';

const SelectValueDisplay = styled.div`
  position: relative;
`;

const SelectDisplay = styled(TriggerButton)`
  height: auto;
  padding: ${spacing.threeQuarter};
  text-align: left;
  ${body2};
  ${smallShadow};
  > svg {
    flex-shrink: 0; 
  }
`;

export const RECOMMENDATIONS_DROPDOWN_TEST_ID = 'nba-recommended-content-dropdown';

export type RecommendedContentDropdownProps = {
  isLoading: boolean;
  messageMedium: MessageMedium;
  suggestions: Suggestion[];
  selectedValue: string;
  onSelectValue: (selectedValue: string) => void;
};

export const RecommendedContentDropdown = ({
  isLoading,
  messageMedium,
  onSelectValue,
  selectedValue,
  suggestions,
}: RecommendedContentDropdownProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const messageHeaderByType = getMessageHeaderByType(messageMedium).toLowerCase();

  const [isDropdownDisplayed, setIsDropdownDisplayed] = useState(false);
  useClickAway(targetRef, () => {
    if (isDropdownDisplayed) {
      setIsDropdownDisplayed(false);
    }
  });

  const handleCopySuggestionClick = (index: number) => {
    const newSelection = index.toString();
    onSelectValue(newSelection);
    setIsDropdownDisplayed(false);
  };

  // todo: fetch and grab original subject line/message from template data
  const originalCopy = 'Why don’t you try something yummy?';

  const recommendations = suggestions.map(({ suggestionText }) => suggestionText);

  return (
    <SelectValueDisplay ref={targetRef}>
      <SelectDisplay
        onClick={() => setIsDropdownDisplayed(true)}
        data-test={RECOMMENDATIONS_DROPDOWN_TEST_ID}
      >
        <span>{getSelectedSuggestion(suggestions, selectedValue)?.suggestionText}</span>
        {isDropdownDisplayed ? <ChevronUp16 /> : <ChevronDown16 />}
      </SelectDisplay>
      {isDropdownDisplayed && (
        <CopyAssistDropdown targetRef={targetRef}>
          <CopyAssistDropdownHeader
            copyType={messageHeaderByType}
            originalCopy={originalCopy}
            isLoading={isLoading}
          />
          <CopyAssistDropdownBodyContent
            generatedOptions={recommendations}
            isLoading={isLoading}
            onCopySuggestionClick={handleCopySuggestionClick}
            selected={selectedValue}
          />
        </CopyAssistDropdown>
      )}
    </SelectValueDisplay>
  );
};
